import { MONTHS, OPTIONS, type IntakeAnswers } from '@upm/domain';
import { ChoiceGroup, NumberField, Select, cx } from '../../ui';
import { useIntake } from '../IntakeContext';
import { breakdownCheck } from '../logic';
import { FormCard, opts, StepHeader, type ErrorMap } from './common';

type Breakdown = NonNullable<IntakeAnswers['breakdown']>;
const PARTS: Array<[keyof Breakdown, string, string]> = [
  ['individual', 'ind', 'Individuals and households'],
  ['business', 'biz', 'Businesses'],
  ['trust', 'trusts', 'Trusts and estates'],
  ['nonprofit', 'npo', 'Nonprofits'],
];

export function BreakdownCheckText({ answers }: { answers: Pick<IntakeAnswers, 'clientsTotal' | 'breakdown'> }) {
  const check = breakdownCheck(answers);
  return (
    <div aria-live="polite" data-testid="breakdown-check" className={cx('mt-2.5 min-h-[1.5em] text-[13px]', check?.tone === 'warn' ? 'text-warn-fg' : 'text-ok-fg')}>
      {check?.text ?? ''}
    </div>
  );
}

export function StepClients({ errors = {} }: { errors?: ErrorMap }) {
  const { answers: a, setAnswers } = useIntake();
  const set = (p: Partial<IntakeAnswers>) => setAnswers(3, p);
  const setPart = (k: keyof Breakdown, n: number | undefined) =>
    set({ breakdown: { individual: null, business: null, trust: null, nonprofit: null, ...a.breakdown, [k]: n ?? null } });
  return (
    <div className="flex flex-col gap-6">
      <StepHeader step={3} title="Your clients and work">Rough numbers are fine. They tell us how much is moving and what has to land first.</StepHeader>
      <FormCard>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-x-5 gap-y-[18px]">
        <NumberField id="clients" label="About how many active clients do you have?" min={0} value={a.clientsTotal} onValue={(n) => set({ clientsTotal: n })} error={errors.clientsTotal} />
        <NumberField id="jobs" label="About how many returns or jobs are open right now?" min={0} value={a.openJobs} onValue={(n) => set({ openJobs: n })} />
      </div>
      <fieldset className="m-0 min-w-0 rounded-xl border border-line-soft bg-page/60 px-4 pb-3 pt-2">
        <legend className="px-1.5 text-[15px] font-semibold">Roughly how do those clients break down?</legend>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3.5">
          {PARTS.map(([k, id, label]) => (
            <NumberField key={k} id={id} small label={<span className="font-normal text-ink-2">{label}</span>} min={0} value={a.breakdown?.[k]} onValue={(n) => setPart(k, n)} />
          ))}
        </div>
        <BreakdownCheckText answers={a} />
      </fieldset>
      </FormCard>
      <FormCard>
      <ChoiceGroup legend="Do you group related clients together?" hint="For example, a married couple and the businesses they own." options={opts(OPTIONS.groups)} value={a.groups} onToggle={(v) => set({ groups: v as IntakeAnswers['groups'] })} />
      <ChoiceGroup legend="Do you use repeatable checklists or job templates?" options={opts(OPTIONS.templates)} value={a.templates} onToggle={(v) => set({ templates: v as IntakeAnswers['templates'] })} />
      <ChoiceGroup legend="How would you describe your client list today?" options={opts(OPTIONS.clientList)} value={a.clientList} onToggle={(v) => set({ clientList: v as IntakeAnswers['clientList'] })} />
      <ChoiceGroup legend="Do you bill clients from the time you track?" options={opts(OPTIONS.timeBilling)} value={a.timeBilling} onToggle={(v) => set({ timeBilling: v as IntakeAnswers['timeBilling'] })} />
      <Select
        id="renewal"
        className="max-w-[320px]"
        label="When do most engagement letters renew?"
        value={a.renewalMonth === undefined ? '' : String(a.renewalMonth)}
        onChange={(e) => set({ renewalMonth: e.target.value === 'varies' ? 'varies' : (Number(e.target.value) as IntakeAnswers['renewalMonth']) })}
      >
        <option value="" disabled>Choose a month</option>
        {MONTHS.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
        <option value="varies">It varies</option>
      </Select>
      </FormCard>
    </div>
  );
}
